import React from 'react';
import styles from '../../styles/ReporteAuditoriaStyles/ReporteAuditoria.module.css';
import { resolverNombrePantalla } from '../../utils/auditoriaLabels';

function formatFecha(str) {
  if (!str || str === 'N/A') return str || '—';
  try {
    const d = new Date(str.replace(' ', 'T'));
    if (isNaN(d.getTime())) return str;
    return d.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return str;
  }
}

/**
 * Tabla del Reporte de Auditoría (paginación manejada por el padre vía API).
 *
 * Props:
 *  resultados: Array     — filas de la página actual
 *  total: number         — total de registros
 *  page: number          — página actual (1-based)
 *  totalPages: number
 *  onPageChange(n)
 *  loading: boolean
 */
function TablaAuditoria({ resultados = [], total = 0, page = 1, totalPages = 1, onPageChange, loading }) {
  if (loading) {
    return (
      <div className={styles.tableSection}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner} />
          <span>Cargando bitácora…</span>
        </div>
      </div>
    );
  }

  const start = resultados.length > 0 ? (page - 1) * 20 + 1 : 0;
  const end = (page - 1) * 20 + resultados.length;

  const buildPages = () => {
    const pages = [];
    const half = 2;
    let from = Math.max(1, page - half);
    let to = Math.min(totalPages, page + half);
    if (to - from < 4) {
      if (from === 1) to = Math.min(totalPages, from + 4);
      else from = Math.max(1, to - 4);
    }
    for (let i = from; i <= to; i++) pages.push(i);
    return pages;
  };

  return (
    <div className={styles.tableSection}>
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>Bitácora de Auditoría</h2>
        {total > 0 && (
          <span className={styles.tableMeta}>
            Mostrando {start}–{end} de {total.toLocaleString('es-MX')} registros
          </span>
        )}
      </div>

      <div className={styles.tableContainer}>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Usuario Creación</th>
              <th>Fecha Creación</th>
              <th>Usuario Modificación</th>
              <th>Fecha Modificación</th>
              <th>Módulo</th>
              <th>Pantalla</th>
              <th>Registro Modificado</th>
            </tr>
          </thead>
          <tbody>
            {resultados.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.noData}>
                  No se encontraron registros con los filtros seleccionados.
                </td>
              </tr>
            ) : (
              resultados.map((r, idx) => (
                <tr key={r.id_bitacora}>
                  <td>{(page - 1) * 20 + idx + 1}</td>
                  <td>{r.usuario_creacion || 'N/A'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.fecha_creacion ? formatFecha(r.fecha_creacion) : '—'}
                  </td>
                  <td>{r.usuario_modificacion || 'N/A'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.fecha_modificacion ? formatFecha(r.fecha_modificacion) : '—'}
                  </td>
                  <td>{r.modulo || '—'}</td>
                  <td title={resolverNombrePantalla(r.pantalla)}>
                    {resolverNombrePantalla(r.pantalla)}
                  </td>
                  <td title={r.descripcion_registro || ''}>
                    {r.descripcion_registro || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            onClick={() => onPageChange(1)}
            disabled={page === 1}
          >
            «
          </button>
          <button
            className={styles.pageBtn}
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            ‹
          </button>
          {buildPages().map((p) => (
            <button
              key={p}
              className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          ))}
          <button
            className={styles.pageBtn}
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            ›
          </button>
          <button
            className={styles.pageBtn}
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
}

export default TablaAuditoria;
